/**
 * Progress note PDF export for client-side download.
 */
import type { Questionnaire, QuestionnaireResponse } from '@medplum/fhirtypes';
import { NOTE_METADATA_SNAPSHOT_EXT, PROVIDER_SIGNATURE_EXT_URL } from '@unify/fhir-model/constants';
import { extractQRTextLines } from '../pages/appointments/addendums/noteDiffUtils';
import { formatNoteMetadataSnapshotLines } from './noteMetadataPdfLines';
import { buildMinimalTextPdf } from './minimalTextPdf';
import {
  getParticipantAttendanceFromResponseItems,
  splitParticipantAttendanceFromResponseItems,
} from '../pages/appointments/utils/participantAttendance';

function readSignatureName(qr: QuestionnaireResponse): string | undefined {
  const ext = qr.extension?.find((e) => e.url === PROVIDER_SIGNATURE_EXT_URL);
  if (!ext?.valueString) return undefined;
  try {
    const parsed = JSON.parse(ext.valueString) as { name?: string };
    return parsed?.name?.trim() || undefined;
  } catch {
    return undefined;
  }
}

function participantAttendanceLines(qr: QuestionnaireResponse): string[] {
  const { attendanceItem } = splitParticipantAttendanceFromResponseItems(qr.item);
  if (!attendanceItem?.item?.length) return [];

  const lines: string[] = ['Participant attendance:'];
  const statuses = getParticipantAttendanceFromResponseItems([attendanceItem]);
  for (const row of attendanceItem.item) {
    const prefix = 'participant-attendance.';
    const patientId = row.linkId?.startsWith(prefix) ? row.linkId.slice(prefix.length) : row.linkId ?? '';
    const name = row.text?.trim() || patientId || 'Participant';
    const status = statuses[patientId] ?? row.answer?.[0]?.valueString?.trim() ?? '—';
    lines.push(`  ${name}: ${status}`);
  }
  return lines;
}

export function buildProgressNotePdfBytes(
  questionnaireResponse: QuestionnaireResponse,
  questionnaire: Questionnaire | null | undefined,
  title: string,
): Uint8Array {
  const sections: string[] = [];

  const metaExt = questionnaireResponse.extension?.find((e) => e.url === NOTE_METADATA_SNAPSHOT_EXT);
  if (metaExt?.valueString) {
    try {
      const meta = JSON.parse(metaExt.valueString) as Record<string, unknown>;
      sections.push(...formatNoteMetadataSnapshotLines(meta));
      sections.push(...participantAttendanceLines(questionnaireResponse));
      sections.push('');
    } catch {
      /* ignore malformed snapshot */
    }
  }

  const signer = readSignatureName(questionnaireResponse);
  if (signer) {
    sections.push(`Signed by: ${signer}`);
    sections.push('');
  }

  const { templateItems } = splitParticipantAttendanceFromResponseItems(questionnaireResponse.item);
  const noteLines = extractQRTextLines(
    { ...questionnaireResponse, item: templateItems },
    questionnaire,
  );
  if (noteLines.length > 0) {
    sections.push('Clinical note:');
    sections.push(...noteLines);
  }

  const body = sections.join('\n').trim() || '(No note body content)';
  return buildMinimalTextPdf(title, body);
}
